import { useEffect, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { publicApi } from "@/lib/api";
import InquiryForm from "@/components/InquiryForm";

const nfEUR = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

export default function Investments() {
  const { t, localized } = useLanguage();
  const [items, setItems] = useState([]);

  useEffect(() => {
    publicApi
      .listProperties({ is_investment: true, limit: 100 })
      .then(setItems)
      .catch(() => setItems([]));
  }, []);

  const open = items.filter((x) => !x.investor_closed);
  const closed = items.filter((x) => x.investor_closed);

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="overline mb-3">{t("investments.label")}</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight text-ink font-light max-w-4xl">
          {t("investments.title")}
        </h1>
        <p className="mt-8 text-lg text-ink max-w-2xl leading-relaxed font-normal">
          {t("investments.body")}
        </p>

        {/* OPEN */}
        <div className="mt-20">
          <div className="flex items-end justify-between mb-8 pb-5 border-b border-line">
            <div>
              <div className="overline mb-2">{t("investments.open")}</div>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight text-ink font-light">
                {open.length} {open.length === 1 ? t("investments_page.opportunities_1") : t("investments_page.opportunities_n")}
              </h2>
            </div>
            <div className="hidden md:block font-mono text-xs text-muted">
              {t("investments_page.reviewed")}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-10">
            {open.map((p, i) => (
              <InvestmentCard key={p.id} p={p} localized={localized} t={t} index={i} />
            ))}
          </div>
        </div>

        {/* CLOSED */}
        {closed.length > 0 && (
          <div className="mt-24 bg-obsidian text-obsidian-text p-8 md:p-14 relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none opacity-30"
              style={{
                backgroundImage:
                  "url(https://images.unsplash.com/photo-1533929736458-ca588d08c8be?auto=format&fit=crop&w=2400&q=80)",
                backgroundSize: "cover", backgroundPosition: "center", filter: "blur(10px) brightness(0.4)"
              }} />
            <div className="relative">
              <div className="overline text-obsidian-accent mb-3">{t("investments.closed")}</div>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight font-light max-w-2xl">
                {t("investments_page.closed_title")}
              </h2>
              <div className="mt-10 grid md:grid-cols-2 gap-8">
                {closed.map((p, i) => (
                  <div
                    key={p.id}
                    data-testid={`investment-closed-${p.reference || p.id}`}
                    className="border border-obsidian-line p-6 relative"
                    style={{ animation: "fadeUp .9s ease-out both", animationDelay: `${i * 80}ms` }}
                  >
                    <div className="overline text-obsidian-muted mb-2">
                      {p.country} · {p.location}
                    </div>
                    <div className="font-serif text-2xl">{localized(p.title)}</div>
                    <div className="mt-4 grid grid-cols-3 gap-4 border-t border-obsidian-line pt-4 text-xs">
                      <Stat dark label={t("investments.yield")} value={p.expected_yield || "—"} />
                      <Stat dark label={t("investments.ticket")} value={p.ticket_size || "—"} />
                      <Stat dark label={t("investments.horizon")} value={p.horizon || "—"} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-24 border-t border-line pt-16 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline mb-3">{t("investments.cta")}</div>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight text-ink font-light">
              {t("investments_page.memorandum_title")}
            </h2>
            <p className="mt-4 text-muted text-sm leading-relaxed max-w-md">
              {t("investments_page.memorandum_body")}
            </p>
          </div>
          <div className="md:col-span-7">
            <InquiryForm testIdPrefix="investments-inquiry" service="investment" />
          </div>
        </div>
      </div>
    </div>
  );
}

function InvestmentCard({ p, localized, t, index }) {
  return (
    <article
      data-testid={`investment-card-${p.reference || p.id}`}
      className="group border border-line bg-white/60 hover:bg-white transition-colors"
      style={{ animation: "fadeUp .9s ease-out both", animationDelay: `${index * 80}ms` }}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={p.images?.[0]}
          alt=""
          loading="lazy"
          className="card-img absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute top-4 left-4 bg-white/90 text-ink text-[10px] tracking-widest2 uppercase px-3 py-1">
          {p.country}
        </div>
      </div>
      <div className="p-6 md:p-8">
        <div className="overline">{p.location}</div>
        <h3 className="font-serif text-2xl md:text-3xl mt-2 leading-tight text-ink">
          {localized(p.title)}
        </h3>
        <p className="mt-4 text-sm text-ink leading-relaxed line-clamp-3">
          {localized(p.description)}
        </p>
        <div className="mt-6 grid grid-cols-3 gap-4 border-t border-line pt-5 text-xs">
          <Stat label={t("investments.yield")} value={p.expected_yield || "—"} />
          <Stat label={t("investments.ticket")} value={p.ticket_size || (p.price ? nfEUR.format(p.price) : "—")} />
          <Stat label={t("investments.horizon")} value={p.horizon || "—"} />
        </div>
      </div>
    </article>
  );
}

function Stat({ label, value, dark = false }) {
  return (
    <div>
      <div className={`font-mono text-sm ${dark ? "text-obsidian-text" : "text-ink"}`}>{value}</div>
      <div className={`overline mt-1 ${dark ? "text-obsidian-muted" : ""}`}>{label}</div>
    </div>
  );
}
